import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Pagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UserTableSkeleton from "../../components/user-management/UserTableSkeleton";
import { setSearchTermAction } from "../app-container/reducer/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { initColumns, toggleColumn } from "../app-container/reducer/visibleColumnsSlice";
import { fetchGoogleAnalyticsList } from "../../api/googleAnalytics";
import SearchFilter from "../../components/analytics-and-insights/SearchFilter";
const visibleColumnKey =   ["sessionManualAdContent","eventName","Profile_gender","screenPageViews","totalUsers","eventCount"]
const ExpenseOverviewTable = () => {
  const fullFilter = useSelector((state) => state.filter);
  const visibleColumns = useSelector(state => state.visibleColumns.pages.google_analytics || {});
  const searchTermFromStore = useSelector((state) => state.filter.searchTerm);
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1);
  const filterWithoutUTM = useMemo(() => {
    const { utm_options, ...rest } = fullFilter;
    setCurrentPage(1)  //reset to pg 1 when filter change
    return rest;
  }, [JSON.stringify(fullFilter)]);
  const tableContainerRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [expenseList, setExpenseList] = useState([])
  const [scrollState, setScrollState] = useState({ scrollLeft: 0, scrollWidth: 0,clientWidth: 0,});
  const debounceTimeoutRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [sortBy, setSortBy] = useState(null);
const [sortOrder, setSortOrder] = useState("asc");


  const [totalPages, setTotalPages] = useState(0)
  const itemsPerPage = 25;

  const updateScrollState = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setScrollState({ scrollLeft, scrollWidth, clientWidth });
    }
  };

  const handleTableScroll = () => updateScrollState();

  const handleStickyScroll = (e) => {
    if (!tableContainerRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;

    const newScrollLeft =
      (clickX / trackRect.width) *
      (scrollState.scrollWidth - scrollState.clientWidth);

    tableContainerRef.current.scrollLeft = newScrollLeft;
  };

  const thumbWidth = useMemo(() => {
    if (scrollState.scrollWidth > scrollState.clientWidth) {
      const ratio = scrollState.clientWidth / scrollState.scrollWidth;
      return Math.max(ratio * scrollState.clientWidth, 50);
    }
    return 100;
  }, [scrollState]);

  const thumbPosition = useMemo(() => {
    if (scrollState.scrollWidth > scrollState.clientWidth) {
      const ratio =
        scrollState.scrollLeft /
        (scrollState.scrollWidth - scrollState.clientWidth);
      return ratio * (scrollState.clientWidth - thumbWidth);
    }
    return 0;
  }, [scrollState, thumbWidth]);

  const handleThumbDrag = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startLeft = scrollState.scrollLeft;

    const handleMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const trackWidth = scrollState.clientWidth - thumbWidth;
      const scrollable =
        scrollState.scrollWidth - scrollState.clientWidth;

      if (trackWidth > 0) {
        const newScrollLeft =
          startLeft + (deltaX / trackWidth) * scrollable;

        tableContainerRef.current.scrollLeft = Math.max(
          0,
          Math.min(newScrollLeft, scrollable)
        );
      }
    };

    const stop = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", stop);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", stop);
  };

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, []);

  const fetchData = async (page = currentPage) => {
    try {
      setLoading(true);
      const data = await fetchGoogleAnalyticsList({filter: {...filterWithoutUTM,sortBy,sortOrder,page, limit: itemsPerPage }});
      setTotalPages(data?.pagination?.totalPages)
      setExpenseList(data?.data)
    } catch (err) {
      console.error('Error while loading user data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage,JSON.stringify(filterWithoutUTM),sortBy,sortOrder]);

    const columnHeaders = [
    { key: "sessionManualAdContent", label: "Ad Content" },
    { key: "eventName", label: "Event Name" },
    { key: "Profile_gender", label: "Profile Gender" },
    { key: "screenPageViews", label: "Page Views" },
    { key: "totalUsers", label: "Total Users" },
    { key: "eventCount", label: "Event Count" },
  ];
    const handleColumnToggle = (key) => {
      dispatch(
        toggleColumn({
          pageKey: "google_analytics",
          column: key
        }));
    };
  
    useEffect(() => {
      dispatch(
        initColumns({
          pageKey: "google_analytics",
          columns: visibleColumnKey
        })
      );
    }, []);

  useEffect(() => {
    if (searchTerm === undefined || searchTerm === null) return

    clearTimeout(debounceTimeoutRef.current);

    debounceTimeoutRef.current = setTimeout(() => {
      dispatch(setSearchTermAction({ searchTerm }))
    }, 500);

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (searchTermFromStore == null && searchTerm) setSearchTerm(null);
  }, [searchTermFromStore])

  const renderSortIcon = (column) => {
  if (sortBy !== column) return "⇅"; // neutral icon
  return sortOrder === "asc" ? "▲" : "▼";
};

const handleSort = (column) => {
  if (sortBy === column) {
    setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
  } else {
    setSortBy(column);
    setSortOrder("asc");
  }
};

  return (
    <>
      <SearchFilter type={"google-analytics"} isHideSelectCam={false} isHideUserManagement={false}/>
      <Box sx={{ width: "320px", ml: 'auto', mt: 3 }} className="d-flex align-items-center gap-2">
        <TextField
          fullWidth
          size="small"
          placeholder="Search ad content..."
          value={searchTerm ?? ""}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ opacity: 0.6 }} />
              </InputAdornment>
            ),
            sx: {
              fontSize: "14px",
              height: "38px",
              borderRadius: "8px",
              background: '#f5f6fa'
            },
          }}
        />
        <div className="dropdown">
          <button
            className="badge text-sm fw-medium border bg-transparent px-10 py-10 radius-6 text-secondary-light"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <ViewColumnOutlinedIcon />
          </button>
          <ul className="dropdown-menu manage-list">
            {columnHeaders.map((column) => (
              <li key={column.key} className="dropdown-item d-flex align-items-center gap-2">
                <input
                  className="form-check-input"
                  style={{ width: '18px', height: '18px' }}
                  type="checkbox"
                  checked={visibleColumns[column.key]}
                  onChange={() => handleColumnToggle(column.key)}
                />
                <label className="ms-2">{column.label}</label>
              </li>
            ))}
          </ul>
        </div>
      </Box>
      {/* TABLE */}
      <div className="table-scroll-container mt-3">
        <div
          ref={tableContainerRef}
          className="custom-horizontal-scroll scroll-sm"
          onScroll={handleTableScroll}
           style={{ 
    maxHeight: 'calc(100vh - 250px)', // Adjust this value as needed
    overflow: 'auto',
    position: 'relative'
  }}
        >
          <table className="table bordered-table sm-table mb-0" style={{position:'relative',}}>
            <thead style={{ position: "sticky", top: 0, zIndex: 10, }}>
              <tr style={{ background: "#f4f4f4", textAlign: "left" }}>
                <th>#</th>

                {visibleColumns?.sessionManualAdContent && (
                  <th >
                    Ad Content
                  </th>
                )}

                {visibleColumns?.eventName && (
                  <th>
                    Event Name
                  </th>
                )}

                {visibleColumns?.Profile_gender && (
                  <th>
                    Profile Gender
                  </th>
                )}

                {visibleColumns?.screenPageViews && (
                  <th onClick={() => handleSort("screenPageViews")} style={{ cursor: "pointer" }}>
                    Page Views {renderSortIcon("screenPageViews")}
                  </th>
                )}

                {visibleColumns?.totalUsers && (
                  <th onClick={() => handleSort("totalUsers")} style={{ cursor: "pointer" }}>
                    Total Users {renderSortIcon("totalUsers")}
                  </th>
                )}

                {visibleColumns?.eventCount && (
                  <th onClick={() => handleSort("eventCount")} style={{ cursor: "pointer" }}>
                    Event Count {renderSortIcon("eventCount")}
                  </th>
                )}
              </tr>

            </thead>

            <tbody>
              {loading ? (
                <UserTableSkeleton
                  rows={10}
                  columns={Object.values(visibleColumns)?.filter(Boolean)?.length}
                />
              ) : expenseList?.length > 0 ? (
                expenseList?.map((item, index) => (
                  <tr key={index}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    {visibleColumns?.sessionManualAdContent && <td>{item?.sessionManualAdContent}</td>}
                    {visibleColumns?.eventName && <td>{item?.eventName}</td>}
                    {visibleColumns?.Profile_gender && <td>{item?.User_Gender}</td>}
                    {visibleColumns?.screenPageViews && <td>{Number(item?.screenPageViews || 0)?.toLocaleString()}</td>}
                    {visibleColumns?.totalUsers && <td>{Number(item?.totalUsers || 0)?.toLocaleString()}</td>}
                    {visibleColumns?.eventCount && <td>{Number(item?.eventCount || 0)?.toLocaleString()}</td>}
                    <td>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="20" className="text-center py-3">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sticky custom scrollbar */}
        {scrollState.scrollWidth > scrollState.clientWidth && (
          <div className="sticky-scroll-container">
            <div className="sticky-scroll-track" onClick={handleStickyScroll}>
              <div
                ref={scrollThumbRef}
                className="sticky-scroll-thumb"
                style={{
                  width: `${thumbWidth}px`,
                  transform: `translateX(${thumbPosition}px)`,
                }}
                onMouseDown={handleThumbDrag}
              />
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && Boolean(totalPages) && <div className="d-flex justify-content-center mt-16">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
           sx={{
    "& .MuiPaginationItem-root": {
      color: "#000",  
      border: "1px solid #ddd",
    },

    "& .Mui-selected": {
      backgroundColor: "#ff4a4a !important",  
      color: "#fff",
    },

    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "#ffe5e5",
    },
  }} 
        />
      </div>}
    </>
  );
};

export default ExpenseOverviewTable;
